import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ReCAPTCHA from "react-google-recaptcha";
import AuthPageLayout from "../components/auth/AuthPageLayout";

export default function SubAccountLogin() {
  const navigate = useNavigate();
  const { studioCode: urlStudioCode } = useParams<{ studioCode: string }>();
  const { subAccountLogin } = useAuth();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    studioCode: urlStudioCode || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Security enhancements
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [csrfToken, setCsrfToken] = useState<string>("");
  const [lastAttempt, setLastAttempt] = useState(0);

  // Security constants
  const LOCKOUT_KEY = "subaccount_login_lockout_until";
  const ATTEMPTS_KEY = "subaccount_login_attempts";
  const LAST_ATTEMPT_KEY = "subaccount_last_attempt";
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
  const MIN_ATTEMPT_INTERVAL = 1000; // 1 second between attempts

  // Add ReCAPTCHA state
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Generate CSRF token on component mount
  useEffect(() => {
    const generateCSRFToken = () => {
      const token = crypto.randomUUID();
      setCsrfToken(token);
      setStoredData("csrf_token", token);
      return token;
    };

    // Check if we already have a valid token
    const existingToken = getStoredData("csrf_token");
    if (existingToken) {
      setCsrfToken(existingToken);
    } else {
      generateCSRFToken();
    }

    // Load stored security data
    const storedLockout = getStoredData(LOCKOUT_KEY);
    const storedAttempts = getStoredData(ATTEMPTS_KEY);
    const storedLastAttempt = getStoredData(LAST_ATTEMPT_KEY);

    if (storedLockout) {
      const lockoutTime = Number(storedLockout);
      if (Date.now() < lockoutTime) {
        setLockoutUntil(lockoutTime);
      } else {
        // Lockout expired, clear stored data
        clearStoredData(LOCKOUT_KEY);
        clearStoredData(ATTEMPTS_KEY);
        clearStoredData(LAST_ATTEMPT_KEY);
        setLockoutUntil(null);
        setAttempts(0);
        setLastAttempt(0);
      }
    }

    if (storedAttempts) {
      setAttempts(Number(storedAttempts));
    }

    if (storedLastAttempt) {
      setLastAttempt(Number(storedLastAttempt));
    }

    // Validate studio code from URL
    if (!urlStudioCode) {
      setError("Invalid staff login URL. Please contact your studio owner for the correct login link.");
      return;
    }

    // Basic validation that studio code exists and is not empty
    if (!urlStudioCode.trim()) {
      setError("Invalid staff login URL. Please contact your studio owner for the correct login link.");
      return;
    }
  }, [urlStudioCode]);

  // Security utility functions
  const getStoredData = (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const setStoredData = (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // SessionStorage not available, using memory-only storage
    }
  };

  const clearStoredData = (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  };

  // Validate origin to prevent CSRF attacks
  const validateOrigin = () => {
    const origin = window.location.origin;
    const referer = document.referrer;

    // Allow requests from same origin
    if (!referer || referer.startsWith(origin)) {
      return true;
    }

    // For development, allow localhost variations
    if (process.env.NODE_ENV === "development") {
      return referer.includes("localhost") || referer.includes("127.0.0.1");
    }

    return false;
  };

  // Validate CSRF token
  const validateCSRFToken = (token: string) => {
    const storedToken = getStoredData("csrf_token");
    return token && storedToken && token === storedToken;
  };

  // Input validation and sanitization
  const sanitizeInput = (input: string) => {
    return input.replace(/[<>]/g, "").trim();
  };

  const validateUsername = (username: string) => {
    // Username should be alphanumeric, underscore, or dash only
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    return (
      username.length >= 3 &&
      username.length <= 20 &&
      usernameRegex.test(username)
    );
  };

  const handleInputChange = (field: string, value: string) => {
    const sanitizedValue = sanitizeInput(value);
    setFormData((prev) => ({
      ...prev,
      [field]: sanitizedValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // CSRF protection
    if (!validateOrigin()) {
      setError(
        "Invalid request origin. Please refresh the page and try again."
      );
      return;
    }

    if (!validateCSRFToken(csrfToken)) {
      setError(
        "Invalid security token. Please refresh the page and try again."
      );
      return;
    }

    // Check if user is currently locked out
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / 60000);
      setError(
        `Too many failed attempts. Please try again in ${remainingTime} minute(s).`
      );
      return;
    }

    // Rate limiting - prevent rapid-fire attempts
    const now = Date.now();
    if (now - lastAttempt < MIN_ATTEMPT_INTERVAL) {
      setError("Please wait a moment before trying again.");
      return;
    }

    // ReCaptcha validation
    if (!recaptchaValue && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      setError("Please complete the reCAPTCHA verification");
      return;
    }

    // Input validation
    if (!validateUsername(formData.username)) {
      setError(
        "Username must be 3-20 characters and contain only letters, numbers, underscores, or dashes."
      );
      return;
    }

    if (formData.studioCode.trim().length < 1) {
      setError("Studio code is required.");
      return;
    }

    if (formData.password.length < 1) {
      setError("Password is required.");
      return;
    }

    setLoading(true);
    setError("");
    setLastAttempt(now);
    setStoredData(LAST_ATTEMPT_KEY, now.toString());

    try {
      await subAccountLogin(
        formData.username,
        formData.password,
        formData.studioCode
      );

      // Reset security counters on successful login
      setAttempts(0);
      clearStoredData(ATTEMPTS_KEY);
      clearStoredData(LOCKOUT_KEY);
      clearStoredData(LAST_ATTEMPT_KEY);
      setLockoutUntil(null);
      
      // Reset ReCAPTCHA
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      navigate("/app", { replace: true });
    } catch (error) {
      // Sub-account login error - error handling without console logging

      // Increment failed attempts
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setStoredData(ATTEMPTS_KEY, newAttempts.toString());

      // Check if we should lock out the user
      if (newAttempts >= MAX_ATTEMPTS) {
        const lockoutTime = Date.now() + LOCKOUT_DURATION;
        setLockoutUntil(lockoutTime);
        setStoredData(LOCKOUT_KEY, lockoutTime.toString());
        setError(
          `Too many failed attempts. Account temporarily locked for 15 minutes.`
        );
      } else {
        setError("Invalid username or password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate lockout feedback
  let lockoutFeedback = null;
  if (lockoutUntil && Date.now() < lockoutUntil) {
    const remainingTime = Math.ceil((lockoutUntil - Date.now()) / 60000);
    lockoutFeedback = `Account locked. Try again in ${remainingTime} minute(s).`;
  }

  return (
    <AuthPageLayout
      title="Staff Login"
      subtitle="Sign in with the studio code and credentials from your studio owner."
      centerHeader
      footerLink={
        <>
          Or{" "}
          <Link to="/login" className="auth-link">
            sign in as main account owner
          </Link>
        </>
      }
    >
        <form className="space-y-5" onSubmit={handleSubmit}>
          {(error || lockoutFeedback) && (
            <div className="auth-alert auth-alert-error">
              {lockoutFeedback || error}
            </div>
          )}

          {!urlStudioCode && (
            <div className="auth-alert auth-alert-error">
              <p className="font-medium">Invalid Staff Login URL</p>
              <p className="mt-1">
                Please contact your studio owner for the correct login link.
              </p>
            </div>
          )}

          <div>
            <label htmlFor="studioCode" className="auth-label">
              Studio Code
            </label>
            <input
              id="studioCode"
              name="studioCode"
              type="text"
              required
              className="auth-input"
              placeholder="Studio Code"
              value={formData.studioCode}
              onChange={(e) => handleInputChange("studioCode", e.target.value)}
              disabled={
                loading || !!(lockoutUntil && Date.now() < lockoutUntil)
              }
            />
          </div>

          <div>
            <label htmlFor="username" className="auth-label">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              maxLength={20}
              className="auth-input"
              placeholder="Username"
              value={formData.username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              disabled={
                loading || !!(lockoutUntil && Date.now() < lockoutUntil)
              }
            />
          </div>

          <div>
            <label htmlFor="password" className="auth-label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="auth-input"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              disabled={
                loading || !!(lockoutUntil && Date.now() < lockoutUntil)
              }
            />
          </div>

          <input type="hidden" name="csrf_token" value={csrfToken} />

          {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
            <div className="auth-recaptcha-wrap">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={setRecaptchaValue}
                onExpired={() => setRecaptchaValue(null)}
                theme="light"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={
              loading ||
              !!(lockoutUntil && Date.now() < lockoutUntil) ||
              !urlStudioCode
            }
            className="auth-btn-primary"
          >
            {loading ? "Signing in..." : "Staff Login"}
          </button>

          {attempts > 0 && attempts < MAX_ATTEMPTS && (
            <div className="auth-alert auth-alert-warning">
              {MAX_ATTEMPTS - attempts} login attempt(s) remaining before
              temporary lockout.
            </div>
          )}
        </form>
      </AuthPageLayout>
  );
}
